import React, { useState, useEffect } from 'react';
import { Form, Button, Badge, Alert, InputGroup, Modal } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Search, Pencil, RotateCcw, Trash2, UserPlus, Send } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

const containerVariants = {
  animate: { transition: { staggerChildren: 0.04 } }
};

const itemVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } }
};

function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    role: '',
    search: '',
    subscription_plan: '',
    is_active: ''
  });
  const [pagination, setPagination] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [sendingInvite, setSendingInvite] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();
  const isMainAdmin = user?.email?.toLowerCase() === 'support@resunize.com';

  useEffect(() => {
    fetchAccounts();
  }, [filters]);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });

      const response = await api.get(`/admin/accounts?${params.toString()}`);
      setAccounts(response.data.users);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Failed to load accounts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (account) => {
    if (!account.is_active) {
      return <Badge bg="danger">Inactive</Badge>;
    }
    if (account.subscription_plan === 'pro') {
      return <Badge bg="success">Pro</Badge>;
    }
    return <Badge bg="secondary">Free</Badge>;
  };

  const handleDelete = async (accountId, email, e) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete the account for "${email}"? This action cannot be undone.`)) return;
    setDeleting(accountId);
    try {
      await api.delete(`/admin/accounts/${accountId}`);
      toast.success(`Account "${email}" deleted`);
      fetchAccounts();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete account');
    } finally {
      setDeleting(null);
    }
  };

  const handleInviteAdmin = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setSendingInvite(true);
    try {
      await api.post('/admin/invite-admin', { email: inviteEmail.trim() });
      toast.success(`Invitation sent to ${inviteEmail.trim()}`);
      setInviteEmail('');
      setShowInviteModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send invitation');
    } finally {
      setSendingInvite(false);
    }
  };

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Accounts</h1>
          <p className="page-subtitle">Manage user accounts and subscriptions</p>
        </div>
        {isMainAdmin && (
          <Button variant="primary" onClick={() => setShowInviteModal(true)} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <UserPlus size={16} /> Invite Admin
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="glass-card" style={{ marginBottom: '24px' }}>
        <div className="filter-bar">
          <Form.Group style={{ flex: 1, minWidth: '200px' }}>
            <Form.Label>Search</Form.Label>
            <InputGroup>
              <InputGroup.Text><Search size={16} /></InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Email, name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </InputGroup>
          </Form.Group>
          <Form.Group style={{ minWidth: '140px' }}>
            <Form.Label>Role</Form.Label>
            <Form.Select
              value={filters.role}
              onChange={(e) => handleFilterChange('role', e.target.value)}
            >
              <option value="">All</option>
              <option value="shadchan">Shadchan</option>
              <option value="single">Single</option>
              <option value="parent">Parent</option>
              <option value="admin">Admin</option>
            </Form.Select>
          </Form.Group>
          <Form.Group style={{ minWidth: '140px' }}>
            <Form.Label>Subscription</Form.Label>
            <Form.Select
              value={filters.subscription_plan}
              onChange={(e) => handleFilterChange('subscription_plan', e.target.value)}
            >
              <option value="">All</option>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
            </Form.Select>
          </Form.Group>
          <Form.Group style={{ minWidth: '120px' }}>
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={filters.is_active}
              onChange={(e) => handleFilterChange('is_active', e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </Form.Select>
          </Form.Group>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Button
              variant="secondary"
              onClick={() => setFilters({ page: 1, limit: 50, role: '', search: '', subscription_plan: '', is_active: '' })}
            >
              <RotateCcw size={14} />
              Clear
            </Button>
          </div>
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {/* Accounts Table */}
      <div className="glass-card" style={{ padding: 0 }}>
        <div style={{
          padding: '18px 24px',
          borderBottom: '1px solid var(--border-light)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h3 className="card-title" style={{ margin: 0 }}>
            Accounts
            <span style={{
              marginLeft: '10px',
              fontSize: '13px',
              color: 'var(--text-muted)',
              fontWeight: 400
            }}>
              {pagination?.total || 0} total
            </span>
          </h3>
        </div>

        <div style={{ padding: '0 8px 8px' }}>
          {loading ? (
            <div className="page-loading" style={{ minHeight: '200px' }}>
              <div className="loading-spinner" />
            </div>
          ) : (
            <>
              <div style={{ overflowX: 'auto' }}>
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Plan</th>
                      <th>Created</th>
                      <th style={{ width: '160px', textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <motion.tbody
                    variants={containerVariants}
                    initial="initial"
                    animate="animate"
                  >
                    {accounts.map((account) => (
                      <motion.tr
                        key={account.id}
                        variants={itemVariants}
                        style={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/accounts/${account.id}`)}
                      >
                        <td>
                          <span style={{ fontWeight: 500 }}>{account.email}</span>
                        </td>
                        <td>{account.first_name} {account.last_name}</td>
                        <td><Badge bg="info">{account.role}</Badge></td>
                        <td>{getStatusBadge(account)}</td>
                        <td>
                          {account.subscription_plan === 'pro' ? (
                            <Badge bg="success">Pro</Badge>
                          ) : (
                            <Badge bg="secondary">Free</Badge>
                          )}
                        </td>
                        <td style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                          {new Date(account.created_at).toLocaleDateString()}
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/accounts/${account.id}`);
                              }}
                            >
                              <Pencil size={13} /> View
                            </Button>
                            {(account.role !== 'admin' || isMainAdmin) && account.email?.toLowerCase() !== 'support@resunize.com' && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                disabled={deleting === account.id || account.email === user?.email}
                                onClick={(e) => handleDelete(account.id, account.email, e)}
                                title={account.email === user?.email ? 'Cannot delete your own account' : 'Delete account'}
                              >
                                <Trash2 size={13} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </motion.tbody>
                </table>
              </div>

              {pagination && pagination.totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '16px 16px 8px'
                }}>
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={pagination.page === 1}
                      onClick={() => handlePageChange(pagination.page - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      disabled={pagination.page === pagination.totalPages}
                      onClick={() => handlePageChange(pagination.page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Invite Admin Modal */}
      <Modal show={showInviteModal} onHide={() => { setShowInviteModal(false); setInviteEmail(''); }} centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '18px', fontWeight: 600 }}>
            <UserPlus size={20} style={{ marginRight: '8px', verticalAlign: 'text-bottom' }} />
            Invite New Admin
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginBottom: '20px' }}>
            Enter the email address of the person you want to invite as an admin. They will receive an email with a link to set up their account.
          </p>
          <Form onSubmit={handleInviteAdmin}>
            <Form.Group>
              <Form.Label>Email Address</Form.Label>
              <Form.Control
                type="email"
                placeholder="admin@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                autoFocus
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowInviteModal(false); setInviteEmail(''); }}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleInviteAdmin}
            disabled={sendingInvite || !inviteEmail.trim()}
          >
            <Send size={14} /> {sendingInvite ? 'Sending...' : 'Send Invitation'}
          </Button>
        </Modal.Footer>
      </Modal>
    </motion.div>
  );
}

export default Accounts;
